import React, { useEffect, useState } from "react";
import { Card, Empty, Typography, Skeleton, Button, Tabs, message } from "antd";
import { CarOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import { useParams, useNavigate } from "react-router-dom";
import VehicleHeader from "./VehicleHeader";
import VehicleInformationTab from "./VehicleInformationTab";
import VehicleWarningsTab from "./VehicleWarningsTab";
import VehicleInfractionsTab from "./VehicleInfractionsTab";
import VehicleOwnerTab from "./VehicleOwnerTab";
import VehicleEditModal from "./VehicleEditModal";
import dayjs from "dayjs";
import "dayjs/locale/fr";
import localizedFormat from "dayjs/plugin/localizedFormat";
import { getVehicleById } from "../../api/vehicle/vehicleRequest";
import PermissionRender from "../../components/PermissionRender";

// Configuration de dayjs
dayjs.locale("fr");
dayjs.extend(localizedFormat);

const VehicleDetails = () => {
  const { vehicleId } = useParams();
  const navigate = useNavigate();
  const [vehicleData, setVehicleData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);

  useEffect(() => {
    // Fonction pour récupérer les détails du véhicule
    const fetchVehicleDetails = async () => {
      try {
        // Récupérer les détails du véhicule
        const response = await getVehicleById(vehicleId);
        setVehicleData(response);
      } catch (error) {
        console.error("Error fetching vehicle details:", error);
        message.error("Impossible de charger les détails du véhicule");
      } finally {
        setLoading(false);
      }
    };

    fetchVehicleDetails();
  }, [vehicleId]);

  const updateVehicleData = (updatedData) => {
    setVehicleData({ ...vehicleData, ...updatedData });
  };

  const showEditModal = () => {
    setIsEditModalVisible(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalVisible(false);
  };

  if (loading) {
    return (
      <div className="bg-gray-50 min-h-screen p-5">
        <div className="max-w-6xl mx-auto">
          <Card className="mb-6 shadow-lg">
            <Skeleton active avatar paragraph={{ rows: 2 }} />
          </Card>
          <Card>
            <Skeleton active paragraph={{ rows: 8 }} />
          </Card>
        </div>
      </div>
    );
  }

  if (!vehicleData) {
    return (
      <div className="bg-gray-50 min-h-screen p-5 flex items-center justify-center">
        <Empty
          description="Aucune donnée de véhicule disponible"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        >
          <Button type="primary" onClick={() => navigate(-1)}>
            Retour
          </Button>
        </Empty>
      </div>
    );
  }

  // Extraire les données du véhicule
  const { warnings, infractions, owner } = vehicleData;

  // Onglets de navigation
  const tabItems = [
    {
      key: "1",
      label: (
        <span className="flex items-center">
          <CarOutlined className="mr-2" /> Informations
        </span>
      ),
      children: (
        <VehicleInformationTab
          vehicleData={vehicleData}
          showEditModal={showEditModal}
          onVehicleUpdate={updateVehicleData}
        />
      ),
    },
    {
      key: "2",
      label: (
        <span>
          Avertissements {warnings?.length > 0 && `(${warnings.length})`}
        </span>
      ),
      children: <VehicleWarningsTab warnings={warnings.sort((a, b) => b.id - a.id)} />,
    },
    {
      key: "3",
      label: (
        <span>
          Infractions {infractions?.length > 0 && `(${infractions.length})`}
        </span>
      ),
      children: <VehicleInfractionsTab infractions={infractions.sort((a, b) => b.id - a.id)} />,
    },
    {
      key: "4",
      label: <span>Propriétaire</span>,
      children: <VehicleOwnerTab owner={owner} />,
    },
  ];

  return (
    <div className="bg-gray-50 min-h-screen p-5">
      <div className="max-w-6xl mx-auto">
        <VehicleHeader
          vehicleData={vehicleData}
          showEditModal={showEditModal}
        />

        <Tabs
          defaultActiveKey="1"
          items={tabItems}
          type="card"
          className="bg-white rounded-lg shadow-md mt-6"
          size="large"
        />

        <PermissionRender requiredPermission={["vehicles", "update"]}>
          <VehicleEditModal
            visible={isEditModalVisible}
            vehicleData={vehicleData}
            onCancel={handleCloseEditModal}
            onUpdateVehicle={updateVehicleData}
          />
        </PermissionRender>
      </div>
    </div>
  );
};

export default VehicleDetails;
